import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { useI18n } from 'core/i18n/I18nProvider';
import { PersonalSummariesPanel } from 'features/result-content/PersonalSummariesPanel';
import { ResultItemsPanel } from 'features/result-content/ResultItemsPanel';
import { TagsPanel } from 'features/result-content/TagsPanel';
import { RESULT_CONTENT_TABS, RESULT_ITEM_TYPES } from 'features/result-content/resultContent';
import { PageHeader } from 'shared/components/Feedback';
import { Tabs } from 'shared/components/Tabs';

export function ResultContentPage() {
  const { t, locale, dir } = useI18n();
  const { hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const canViewContent = hasPermission(PERMISSIONS.resultContentView);
  const canManageContent = hasPermission(PERMISSIONS.resultContentManage);
  const canManageTags = hasPermission(PERMISSIONS.tagsManage);
  const canViewTags = canViewContent || canManageTags;

  const tabs = useMemo(() => {
    return RESULT_CONTENT_TABS
      .filter((tab) => {
        if (tab.id === 'tags') {
          return canViewTags;
        }
        return canViewContent;
      })
      .map((tab) => ({ id: tab.id, label: t(tab.labelKey) }));
  }, [canViewContent, canViewTags, t]);

  const requested = params.get('tab');
  const active = tabs.some((tab) => tab.id === requested) ? requested : (tabs[0]?.id || 'summaries');

  const setTab = (id) => {
    const next = new URLSearchParams(params);
    next.set('tab', id);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader title={t('resultContent.title')} description={t('resultContent.description')} />
      <Tabs tabs={tabs} value={active} onChange={setTab} label={t('resultContent.title')} dir={dir} />
      {active === 'summaries' && canViewContent ? (
        <PersonalSummariesPanel t={t} locale={locale} dir={dir} canManage={canManageContent} />
      ) : null}
      {RESULT_ITEM_TYPES.includes(active) && canViewContent ? (
        <ResultItemsPanel t={t} locale={locale} dir={dir} canManage={canManageContent} type={active} />
      ) : null}
      {active === 'tags' && canViewTags ? (
        <TagsPanel t={t} locale={locale} canManage={canManageTags} canView={canViewTags} />
      ) : null}
    </div>
  );
}
